import React from "react";
import { Link } from "react-router-dom";

const Commercial = () => {
  const services = [
    {
      title: "Office Cleaning",
      description:
        "Professional cleaning for corporate offices, coworking spaces, and business centers",
      icon: "🏢",
    },
    {
      title: "Retail Spaces",
      description:
        "Keep your store spotless and inviting for customers with regular cleaning services",
      icon: "🛍️",
    },
    {
      title: "Medical Facilities",
      description:
        "Specialised sanitization for clinics, dental offices, and healthcare environments",
      icon: "🏥",
    },
    {
      title: "Restaurants & Cafes",
      description:
        "Comprehensive kitchen and dining area cleaning to meet health standards",
      icon: "🍽️",
    },
    {
      title: "Warehouses",
      description:
        "Industrial cleaning solutions for large storage and distribution facilities",
      icon: "🏭",
    },
    {
      title: "Gyms & Fitness Centers",
      description:
        "Daily sanitization services for equipment, locker rooms, and common areas",
      icon: "💪",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-900 via-navy-800 to-cyan-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="relative container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl text-white">
            <div className="inline-block bg-cyan-400/20 backdrop-blur-sm border border-cyan-400/40 rounded-full px-6 py-2 mb-6">
              <span className="text-sm font-bold tracking-wide">
                B2B CLEANING SOLUTIONS
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black leading-none mb-6">
              Commercial <span className="text-cyan-400">Cleaning</span>{" "}
              Services
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              Professional cleaning solutions that keep your business running
              smoothly and looking its best.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/get-quote"
                className="px-8 py-4 bg-cyan-400 text-navy-900 font-bold rounded-xl hover:bg-cyan-500"
              >
                REQUEST A QUOTE
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent border-2 border-white text-white font-bold rounded-xl hover:bg-white/10"
              >
                SCHEDULE CONSULTATION
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-4">
              Commercial{" "}
              <span className="text-cyan-500">Facilities We Serve</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tailored cleaning solutions for every type of business
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200 hover:border-cyan-400 shadow-lg hover:shadow-2xl"
              >
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-2xl font-bold text-navy-900 mb-3 group-hover:text-cyan-500">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-navy-900 mb-6">
                Why Businesses <span className="text-cyan-500">Trust Us</span>
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: "Flexible Scheduling",
                    description:
                      "After-hours, weekends, or during business hours - we work around your schedule to minimize disruption.",
                  },
                  {
                    title: "Trained Professionals",
                    description:
                      "Fully vetted, uniformed staff trained in commercial cleaning standards and customer service.",
                  },
                  {
                    title: "Custom Cleaning Plans",
                    description:
                      "Every business is unique. We create tailored cleaning programs that match your specific needs.",
                  },
                  {
                    title: "Quality Assurance",
                    description:
                      "Regular inspections and feedback systems ensure consistently high standards.",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-cyan-400 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-black text-xl">✓</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-navy-900 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-cyan-400 to-navy-900 rounded-3xl p-12 text-white">
              <h3 className="text-3xl font-black mb-6">Service Includes</h3>
              <ul className="space-y-4">
                {[
                  "Desk and workstation sanitization",
                  "Floor vacuuming and mopping",
                  "Restroom deep cleaning and restocking",
                  "Kitchen and break room maintenance",
                  "Trash removal and recycling",
                  "Window and glass surface cleaning",
                  "Conference room setup and cleaning",
                  "High-touch surface disinfection",
                  "Reception area maintenance",
                  "Emergency spill response",
                ].map((item, index) => (
                  <li key={index} className="flex items-center space-x-3">
                    <span className="w-2 h-2 bg-white rounded-full"></span>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-navy-900 text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Let's Partner for a{" "}
            <span className="text-cyan-400">Cleaner Business</span>
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Get a custom quote tailored to your facility's needs
          </p>
          <Link
            to="/get-quote"
            className="inline-block px-12 py-5 bg-cyan-400 text-navy-900 font-black text-lg rounded-xl hover:bg-cyan-500 shadow-2xl"
          >
            SCHEDULE FREE CONSULTATION
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Commercial;
